/**
 * Small shared pieces. Presentation only — no decisions are made in this file.
 */

import { useEffect, useState } from 'react';

import type { PhotoRow } from '../db/database.js';
import type { FieldRepository } from '../db/repository.js';
import { styles, toneStyles } from './styles.js';
import type { StatusLine } from './status-text.js';

/** A status pill. Always carries words — colour is never the only signal. */
export function Chip({ status }: { status: StatusLine }) {
  return <span style={{ ...styles.chip, ...toneStyles[status.tone] }}>{status.text}</span>;
}

export function Banner({ status }: { status: StatusLine }) {
  return (
    <div style={{ ...styles.banner, ...toneStyles[status.tone] }} role="status" data-testid="sync-banner">
      <span>{status.text}</span>
      {status.detail !== undefined && <span style={styles.bannerDetail}>{status.detail}</span>}
    </div>
  );
}

/**
 * A thumbnail rendered from the bytes on this device.
 *
 * Reads local storage rather than a remote URL, and that is the point: the photo a rep just
 * took is visible immediately, with no signal, from the same copy the checksum was computed
 * over. Once object storage exists this stays as-is — the local copy is the authoritative
 * one until an upload is confirmed.
 *
 * The object URL is revoked on unmount. A capture session scrolling through forty shots
 * leaks forty decoded images otherwise, and the phones this runs on do not have the memory
 * to spare.
 */
export function PhotoThumbnail({
  repository,
  photo,
}: {
  repository: FieldRepository;
  photo: PhotoRow;
}) {
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    let alive = true;
    let created: string | undefined;

    repository
      .photoBytes(photo.photoId)
      .then((bytes) => {
        if (!alive || bytes === undefined) return;
        created = URL.createObjectURL(new Blob([bytes as BlobPart], { type: photo.contentType }));
        setUrl(created);
      })
      .catch(() => {
        // A thumbnail that will not render is a display problem, not an evidence problem.
        // The bytes and the checksum are unaffected, so this stays quiet rather than
        // alarming a rep about a photo that is in fact safely stored.
      });

    return () => {
      alive = false;
      if (created !== undefined) URL.revokeObjectURL(created);
    };
  }, [repository, photo.photoId, photo.contentType]);

  if (url === undefined) {
    return <div style={styles.thumb} aria-hidden="true" />;
  }

  return (
    <img
      src={url}
      alt={`Photo captured at ${photo.capturedAt}`}
      style={styles.thumb}
      loading="lazy"
    />
  );
}
